import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, Panel, Button, Field, ConfirmDialog, useToast } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { post, put } from '../../lib/api'
import { money, dateShort } from '../../lib/format'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { BusinessStep, type JoinState } from './Signup'
import { ClientAlerts } from './ClientAlerts'

/** The trial as the server works it out (lib/trial.js info). */
export type TrialInfo = {
  running: boolean; started_at: string; ends_at: string; cap_cents: number; used_cents: number; left_cents: number; full: boolean
  billed_at: string | null; cancelled_at: string | null; can_cancel: boolean; deposit_cents: number | null
}

/**
 * The business's trial on its Billing page: what it has used of its allowance,
 * when it ends and what happens then, and the button that cancels it for a full
 * refund of the deposit.
 */
export function TrialPanel({ trial, onChanged }: { trial: TrialInfo | null; onChanged: () => void }) {
  const toast = useToast()
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  if (!trial) return null
  if (trial.cancelled_at) {
    return (
      <Panel>
        <h2 className="panel-title">Your trial is cancelled</h2>
        <p style={{ marginBottom: 0 }}>Cancelled on {dateShort(trial.cancelled_at)}. {trial.deposit_cents ? `Your ${money(trial.deposit_cents)} deposit was refunded to your card; banks usually show it within 5 to 10 business days. ` : ''}New leads have stopped and nothing more will be charged. The leads you received are yours to keep.</p>
      </Panel>
    )
  }
  if (!trial.running) return null
  const pct = trial.cap_cents ? Math.min(100, Math.round((trial.used_cents / trial.cap_cents) * 100)) : 0
  const cancel = async () => {
    setBusy(true)
    try {
      await post('/api/client/trial/cancel', { confirm: true })
      toast(`Cancelled. Your ${money(trial.deposit_cents ?? 0)} deposit is on its way back to your card.`, 'pos')
      onChanged()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false); setAsking(false) }
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Your trial</h2><span className="muted">until {dateShort(trial.ends_at)}</span></div>
      <div className="cost-rows">
        <div className="cost-row"><span>Leads so far</span><span className="num">{money(trial.used_cents)} of {money(trial.cap_cents)}</span></div>
      </div>
      <div className="trial-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Trial leads used"><span style={{ width: `${pct}%` }} /></div>
      {trial.full
        ? <p style={{ marginBottom: 0 }}>You have all your trial leads. New leads start again when the trial ends on {dateShort(trial.ends_at)}.</p>
        : <p className="muted" style={{ marginBottom: 0 }}>Nothing else is charged in your trial. When it ends, your next {money(trial.deposit_cents ?? 0)} tab is charged and its leads are invoiced, never more than {money(trial.cap_cents)}: your tab credit pays first, the rest goes to your card, and from then on leads are invoiced every week.</p>}
      {trial.can_cancel && (
        <div className="spread wrap-gap" style={{ marginTop: 'var(--s-5)', alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: 13 }}>Not for you? Cancel before {dateShort(trial.ends_at)} and your {money(trial.deposit_cents ?? 0)} deposit is refunded in full.</span>
          <Button variant="secondary" disabled={busy} onClick={() => setAsking(true)}>Cancel trial</Button>
        </div>
      )}
      {asking && (
        <ConfirmDialog tone="neg" title="Cancel your trial?" confirmLabel={busy ? 'Cancelling' : `Cancel and refund ${money(trial.deposit_cents ?? 0)}`} cancelLabel="Keep my trial"
          body={`New leads stop now and your account closes. Your ${money(trial.deposit_cents ?? 0)} deposit is refunded to your card, and the leads you already have are yours, not billed.`}
          onConfirm={cancel} onCancel={() => setAsking(false)} />
      )}
    </Panel>
  )
}

type Todo = { key: string; label: string; href: string }

type Setup = { todo: Todo[]; has_password: boolean; trade_pending: string | null; areas: { id: string; name: string; chosen: boolean }[] }

/**
 * Finish setup: what the two-minute sign-up left for later. Lead alerts on this
 * phone, a password, the areas it serves (every one to start), the business
 * details, then the profile.
 */
export default function ClientSetup() {
  const j = useApi<JoinState>('/api/client/join')
  const s = useApi<Setup>('/api/client/setup')
  const toast = useToast()
  if (j.error) return <ErrorBlock error={j.error} />
  if (!j.data || !s.data) return <LoadingBlock />
  const profileLeft = s.data.todo.some((t) => t.key === 'profile')
  return (
    <>
      <PageHeader title="Finish setting up" subtitle="A few minutes, whenever suits you." />
      <div className="stack">
        {s.data.trade_pending && (
          <Panel>
            <h2 className="panel-title">Your trade: {s.data.trade_pending}</h2>
            <p style={{ marginBottom: 0 }}>We are setting this up for you. Your leads start as soon as it is set.</p>
          </Panel>
        )}
        <Panel>
          <h2 className="panel-title">Lead alerts on this phone</h2>
          <ClientAlerts hasPassword={s.data.has_password} onChange={() => s.reload()} />
        </Panel>
        {!s.data.has_password && <SetPassword onDone={() => { s.reload(); toast('Password set. You can sign in on any device.', 'pos') }} />}
        <Areas areas={s.data.areas} onSaved={() => { s.reload(); toast('Areas saved', 'pos') }} />
        <BusinessStep d={j.data} onSaved={() => { j.reload(); s.reload(); toast('Saved', 'pos') }} />
        {profileLeft && (
          <Panel>
            <h2 className="panel-title">Your business profile</h2>
            <p>What you do best and the jobs you want. It is how we match you to the right leads.</p>
            <Link className="btn btn-primary" to="/portal/profile">Fill in your profile</Link>
          </Panel>
        )}
      </div>
    </>
  )
}

function SetPassword({ onDone }: { onDone: () => void }) {
  const toast = useToast()
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    try { await post('/api/client/setup/password', { password: pw }); setPw(''); onDone() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Panel>
      <h2 className="panel-title">Choose a password</h2>
      <p className="muted" style={{ marginTop: 0 }}>So you can sign in on any device. We also emailed you a link to do this.</p>
      <div className="row-tight" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="Password (at least 8 characters)" id="s-pw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <Button onClick={save} disabled={busy || pw.length < 8}>{busy ? 'Saving' : 'Set password'}</Button>
      </div>
    </Panel>
  )
}

function Areas({ areas, onSaved }: { areas: Setup['areas']; onSaved: () => void }) {
  const toast = useToast()
  const [chosen, setChosen] = useState<string[]>(() => areas.filter((a) => a.chosen).map((a) => a.id))
  const [busy, setBusy] = useState(false)
  if (!areas.length) return null
  const toggle = (id: string) => setChosen(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id])
  const changed = chosen.length !== areas.filter((a) => a.chosen).length || areas.some((a) => a.chosen !== chosen.includes(a.id))
  const save = async () => {
    setBusy(true)
    try { await put('/api/client/setup/areas', { area_ids: chosen }); onSaved() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Panel>
      <h2 className="panel-title">Where you take jobs</h2>
      <p className="muted" style={{ marginTop: 0 }}>Leads come from the areas you choose. Tap to turn one off or on.</p>
      <div className="filter-chips">
        {areas.map((a) => <button key={a.id} type="button" className="filter-chip" aria-pressed={chosen.includes(a.id)} onClick={() => toggle(a.id)}>{a.name}</button>)}
      </div>
      {changed && <div className="row-tight" style={{ marginTop: 'var(--s-4)' }}><Button onClick={save} disabled={busy || !chosen.length}>{busy ? 'Saving' : 'Save areas'}</Button></div>}
    </Panel>
  )
}
